import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { NotificationManager, PaymentV2Api } from '@atlas/core';
import { Button, DefaultPopup, HeaderTitle, Input, Label } from '@atlas/ui';

import { CLABE_WITHDRAWAL_LENGTH } from 'common/constants';
import PaymentMethodItem from 'src/components/ClientProfile/PaymentTab/PaymentMethodItem';

import { displayWithdrawalSuccessNotification } from '../withdrawalUtils';

const validateClabeKey = value => {
  if (!value) return { message: 'required' };

  const errorMaxLength = ruleOption => ({ message: 'max_length', params: { ruleOption } });
  const errorMinLength = ruleOption => ({ message: 'min_length', params: { ruleOption } });
  const errorNumeric = ruleOption => ({ message: 'numeric', params: { ruleOption } });

  if (!+value) return errorNumeric();
  if (value.length < CLABE_WITHDRAWAL_LENGTH) return errorMinLength(CLABE_WITHDRAWAL_LENGTH);
  if (value.length > CLABE_WITHDRAWAL_LENGTH) return errorMaxLength(CLABE_WITHDRAWAL_LENGTH);
  return null;
};

const SpeiWithdrawalPopup = ({ data = {}, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl, withValidation } = data;
  const { amount, currency, partnerWithdrawClabe } = qs.parse(redirectUrl);
  const [userClabe, setUserClabe] = useState(partnerWithdrawClabe);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const amountValue = useMemo(
    () => parseInt(typeof amount === 'string' ? amount : amount[0]),
    [amount],
  );

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handleChangeClabeKey = useCallback(
    e => {
      const value = e.target.value.trim();
      if (withValidation) {
        const error = validateClabeKey(value);
        setError(error);
      }
      setUserClabe(value);
    },
    [withValidation],
  );

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;

    const requestData = {
      additionalParameters: {
        partnerWithdrawClabe: userClabe,
        publicPaymentId,
      },
      amount: amountValue,
      currency,
      pmId,
    };

    setIsSubmitting(true);

    try {
      const response = await PaymentV2Api.createAsyncWithdrawalV2(requestData);
      displayWithdrawalSuccessNotification({ amount, currency, paymentMethod, response });
    } catch (error) {
      NotificationManager.notify({
        message: 'failed_transaction',
        status: 'error',
        type: 'notification',
      });
    } finally {
      handleClose();
    }
  }, [
    amount,
    amountValue,
    currency,
    handleClose,
    isSubmitting,
    paymentMethod,
    pmId,
    publicPaymentId,
    userClabe,
  ]);

  return (
    <DefaultPopup
      className="spei-modal"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="block-big popup-payment-method">
        <PaymentMethodItem amount={amountValue} paymentMethod={paymentMethod} showAmount />
      </HeaderTitle>
      <div className="popup-content">
        <div className="popup-input-container">
          <Input
            active={isActiveInput}
            className="popup-input"
            error={error}
            label="withdrawal_clabe"
            onBlur={() => setIsActiveInput(false)}
            onChange={handleChangeClabeKey}
            onFocus={() => setIsActiveInput(true)}
            value={userClabe}
          />

          {error && (
            <div className="help-text-container">
              <Label message={error.message} params={error.params} />
            </div>
          )}
        </div>
      </div>

      <div className="popup__footer">
        <Button
          className="primary big block"
          disabled={!userClabe || error || isSubmitting}
          label="ok"
          loading={isSubmitting}
          onClick={handleClickButton}
        />
      </div>
    </DefaultPopup>
  );
};

SpeiWithdrawalPopup.propTypes = {
  data: PropTypes.object.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default SpeiWithdrawalPopup;
